import { parseAddress } from '@featit/shared/addressing/paths';

/**
 * What a pathname asks of the public read surface (F-0338 AC-PV21): a work area's address, and the
 * item it names when it names one. Anything else — a namespace, an app route, an entry door — is not
 * a public address, and a signed-out visitor to it is sent to sign in as before.
 *
 * ⚠️ Any `/<namespace>/<slug>` reads as a work area here, because the grammar cannot tell a Public one
 * from a private one; the API says which, with one answer for every kind of "no" (AC-PV6). The cost is
 * one signed-out request for an address that turns out to be nothing, and the same sign-in as before.
 */
export interface IPublicAddress {
  /** `acme/tools/tracker`. */
  readonly reference: string;
  /** The item key when the route is `/items/<key>`, else null. */
  readonly itemKey: string | null;
  /**
   * What the signed-out surface serves at this address (I-1506): the list (`list`) at the work area's
   * home and at `/-/items`, the list address a member sees and shares (I-1529); one item (`item`); and
   * for every other route of the work area — settings, members, documents, a decision — `private`,
   * which draws the sign-in page and never the list under an address that is not the list's. One rule,
   * here; nothing else decides which routes a visitor is served.
   */
  readonly surface: PublicSurfaceType;
}

export type PublicSurfaceType = 'list' | 'item' | 'private';

/** The routes of a work area that are its list: the home, and the items list members share. */
const PUBLIC_LIST_ROUTES: readonly string[] = ['', '/items'];

export function publicAddressOf(pathname: string): IPublicAddress | null {
  const address = parseAddress(pathname.split('?')[0].split('#')[0]);
  if (address.kind !== 'work-area') return null;
  const item = /^\/items\/([^/]+)\/?$/.exec(address.route);
  const surface: PublicSurfaceType = item
    ? 'item'
    : PUBLIC_LIST_ROUTES.includes(address.route)
      ? 'list'
      : 'private';
  return {
    reference: address.reference,
    itemKey: item ? decodeURIComponent(item[1]) : null,
    surface,
  };
}

/** The longest search the public list takes, as its search box does. */
export const PUBLIC_SEARCH_MAX_CHARACTERS = 100;

/**
 * The search a list address carries into the public list (I-1529): a member's list link keeps its
 * text search (`?q=`), the one filter the public list has; every other filter of the members' list —
 * type, state, status, release, part, responsible — is dropped, and the public list shows every
 * published item. The address itself is left as it is, so signing in returns the member to their own
 * filtered list.
 */
export function publicListSearchOf(search: string): string {
  return (new URLSearchParams(search).get('q') ?? '').slice(0, PUBLIC_SEARCH_MAX_CHARACTERS);
}

/** Where the work area's own address is, for a link back from an item. */
export function publicWorkAreaPath(reference: string): string {
  return `/${reference}`;
}

/** Where an item of a Public work area opens: the app's own item route for that address. */
export function publicItemPath(reference: string, key: string): string {
  return `/${reference}/-/items/${encodeURIComponent(key)}`;
}
